import React from 'react'
import styled from 'styled-components'
import { Box, Icon, muiBankTheme } from '@ally/metronome-ui'
import { muiChevronUp, muiChevronDown } from '@ally/metronome-icons'
import { toKebabCase } from '@ally/utilitarian'
import { RefItem } from '.'
import { DataType, DataTypeName } from '../../../types'
import { getDisplayName } from '../../../utils'

const ButtonGroup = styled(Box)`
  ${({ theme: { space } }): string => `
    display: flex;
    align-items: center;
    margin: ${space.xs} 1px ${space.xs} ${space.xs};

    button {
      height: 45px;
      width: 45px;
      min-width: 45px;
      padding: 0;
      background-color: ${muiBankTheme.colors['light-gray']};
      border: 1px solid ${muiBankTheme.colors['slate-3']};
      border-radius: 0;

      &:focus {
        z-index: 1;
      }

      &:first-child {
        border-right: none;
      }

      &[aria-disabled="true"] {
        background-color: ${muiBankTheme.colors['slate-1']};
      }
    }
  `};
`

export const StyledButton = styled.button(
  ({ theme: { buttons } }) => buttons.outline,
)

interface ReorderButtonsProps {
  refObj: RefItem
  index: number
  item: DataType<DataTypeName>
  moveItem: (indexToMove: number, refKey: string, direction: string) => void
  disabledBtns?: string
}

const ReorderButtons: React.FC<ReorderButtonsProps> = ({
  refObj,
  index,
  item,
  moveItem,
  disabledBtns,
}) => {
  const { name } = item
  const firstBtnDisabled = disabledBtns === 'both' || disabledBtns === 'first'
  const lastBtnDisabled = disabledBtns === 'both' || disabledBtns === 'last'

  return (
    <ButtonGroup>
      <StyledButton
        ref={refObj.up}
        onClick={(): void => {
          if (!firstBtnDisabled) {
            moveItem(index, toKebabCase(name), 'up')
          }
        }}
        aria-label={`Move ${getDisplayName(name)} up a position`}
        aria-disabled={firstBtnDisabled ? 'true' : 'false'}
        data-allytmln={`${name} up button`}
      >
        <Icon
          icon={muiChevronUp}
          size="xl"
          fill={
            (firstBtnDisabled
              ? muiBankTheme.colors['slate-3']
              : muiBankTheme.colors
                  .bluesuedeshoes) as keyof typeof muiBankTheme.colors
          }
        />
      </StyledButton>
      <StyledButton
        ref={refObj.down}
        onClick={(): void => {
          if (!lastBtnDisabled) {
            moveItem(index, toKebabCase(name), 'down')
          }
        }}
        aria-label={`Move ${getDisplayName(name)} down a position`}
        aria-disabled={lastBtnDisabled ? 'true' : 'false'}
        data-allytmln={`${name} down button`}
      >
        <Icon
          icon={muiChevronDown}
          size="xl"
          fill={
            (lastBtnDisabled
              ? muiBankTheme.colors['slate-3']
              : muiBankTheme.colors
                  .bluesuedeshoes) as keyof typeof muiBankTheme.colors
          }
        />
      </StyledButton>
    </ButtonGroup>
  )
}

export default ReorderButtons
